/**
 * proposalKanbanColumns - Definição única das colunas fixas do Kanban de Propostas
 *
 * As colunas são fixas e representam 1:1 as situações do enum `crm_proposal_status`.
 * Espelha a definição usada no Kanban de Contratos (contractKanbanColumns.ts).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import type { ProposalStatus } from '@/hooks/useDealProposals';

/** Metadados de uma coluna do Kanban */
export interface ProposalKanbanColumnDef {
  /** Situação enviada para a RPC (chave da coluna) */
  status: ProposalStatus;
  /** Rótulo exibido no cabeçalho */
  label: string;
  /** Cor sólida do indicador (bolinha) */
  barClass: string;
  /** Fundo suave (tint) do cabeçalho */
  headerClass: string;
}

/**
 * Colunas fixas, na ordem do ciclo de vida da proposta.
 */
export const PROPOSAL_KANBAN_COLUMNS: ProposalKanbanColumnDef[] = [
  {
    status: 'draft',
    label: 'Rascunho',
    headerClass: 'bg-muted-foreground/10',
    barClass: 'bg-muted-foreground/40',
  },
  {
    status: 'generated',
    label: 'Gerada',
    headerClass: 'bg-blue-500/10',
    barClass: 'bg-blue-500',
  },
  {
    status: 'sent',
    label: 'Enviada',
    headerClass: 'bg-indigo-500/10',
    barClass: 'bg-indigo-500',
  },
  {
    status: 'viewed',
    label: 'Visualizada',
    headerClass: 'bg-amber-500/10',
    barClass: 'bg-amber-500',
  },
  {
    status: 'accepted',
    label: 'Aceita',
    headerClass: 'bg-emerald-500/10',
    barClass: 'bg-emerald-500',
  },
  {
    status: 'rejected',
    label: 'Recusada',
    headerClass: 'bg-destructive/10',
    barClass: 'bg-destructive',
  },
  {
    status: 'expired',
    label: 'Expirada',
    headerClass: 'bg-orange-500/10',
    barClass: 'bg-orange-500',
  },
  {
    status: 'cancelled',
    label: 'Cancelada',
    headerClass: 'bg-muted-foreground/10',
    barClass: 'bg-muted-foreground/40',
  },
  {
    status: 'superseded',
    label: 'Substituída',
    headerClass: 'bg-muted-foreground/10',
    barClass: 'bg-muted-foreground/40',
  },
];

/** Conjunto de situações válidas (usado pelo Realtime) */
export const PROPOSAL_KANBAN_STATUSES = new Set<string>(
  PROPOSAL_KANBAN_COLUMNS.map((c) => c.status),
);

/**
 * Resolve a coluna de uma situação. Retorna null quando desconhecida.
 */
export function resolveProposalStatusColumn(status?: string | null): ProposalStatus | null {
  if (!status) return null;
  return PROPOSAL_KANBAN_STATUSES.has(status) ? (status as ProposalStatus) : null;
}
